import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, XCircle, Heart, MinusCircle, PlusCircle, Loader2, Send } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;

const Screen = ({ title, subtitle, tone = "neutral", testid }) => (
  <div className="min-h-screen bg-black text-white flex items-center justify-center px-6 py-16">
    <div className="max-w-md text-center" data-testid={testid}>
      <p className="eyebrow mb-3">Votre invitation</p>
      <h1 className={`font-serif-luxe text-3xl mb-4 ${tone === "warm" ? "gold-text" : ""}`}>{title}</h1>
      <p className="text-white/60 text-sm leading-relaxed">{subtitle}</p>
    </div>
  </div>
);

export default function GuestRsvp() {
  const { token } = useParams();
  const [state, setState] = useState({ loading: true });
  const [rsvp, setRsvp] = useState(null); // "confirmed" | "declined" | null
  const [adults, setAdults] = useState(1);
  const [children, setChildren] = useState(0);
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [thankYou, setThankYou] = useState(false);

  useEffect(() => {
    if (!token) return;
    axios.get(`${API}/guest/rsvp/${token}`)
      .then((r) => {
        const g = r.data.guest || {};
        setState({ loading: false, data: r.data });
        setRsvp(g.current_rsvp === "confirmed" || g.current_rsvp === "declined" ? g.current_rsvp : null);
        setAdults(g.adults ?? 1);
        setChildren(g.children ?? 0);
        setNote(g.note || "");
      })
      .catch((e) => {
        setState({ loading: false, error: e?.response?.status || 500 });
      });
  }, [token]);

  if (state.loading) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <Loader2 size={22} className="animate-spin text-[#DCB457]" />
      </div>
    );
  }
  if (state.error === 404) return <Screen title="Lien invalide" subtitle="Ce lien n'est pas reconnu ou a été retiré. Contactez les mariés pour recevoir un nouveau lien." testid="rsvp-invalid" />;
  if (state.error === 410) return <Screen title="Lien indisponible" subtitle="Ce lien a expiré ou a été révoqué. Contactez les mariés pour obtenir un nouveau lien." testid="rsvp-expired" />;
  if (state.error === 429) return <Screen title="Trop de requêtes" subtitle="Merci de patienter quelques instants avant de réessayer." testid="rsvp-ratelimit" />;
  if (state.error) return <Screen title="Erreur réseau" subtitle="Nous n'avons pas pu charger votre invitation. Merci de réessayer dans un instant." testid="rsvp-error" />;

  const ev = state.data.event || {};
  const g = state.data.guest || {};

  if (thankYou) {
    return (
      <div className="min-h-screen bg-black text-white relative overflow-hidden">
        <div className="max-w-xl mx-auto px-6 py-24 text-center relative z-10" data-testid="rsvp-thanks">
          <Heart size={36} className="mx-auto text-[#DCB457] mb-6" strokeWidth={1.3} />
          <p className="eyebrow">Votre réponse est enregistrée</p>
          <h1 className="font-serif-luxe text-4xl mt-4 mb-4">Merci {g.prenom} <span className="italic gold-text">❤</span></h1>
          <p className="text-white/70 text-sm leading-relaxed">
            {rsvp === "confirmed"
              ? "Nous avons hâte de partager ce moment avec vous."
              : "Merci de nous avoir prévenus. Vous nous manquerez."}
          </p>
          <p className="text-white/40 text-xs italic mt-8">Vous pouvez fermer cette page.</p>
        </div>
      </div>
    );
  }

  const submit = async () => {
    if (!rsvp || submitting) return;
    setSubmitting(true);
    try {
      const body = {
        rsvp_status: rsvp,
        adults: rsvp === "confirmed" ? Number(adults) || 0 : 0,
        children: rsvp === "confirmed" ? Number(children) || 0 : 0,
        note: (note || "").slice(0, 500),
      };
      await axios.patch(`${API}/guest/rsvp/${token}`, body);
      setThankYou(true);
    } catch (e) {
      if (e?.response?.status === 410) setState({ ...state, error: 410, loading: false });
      else alert(e?.response?.data?.detail || "Envoi impossible. Merci de réessayer.");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen bg-black text-white" data-testid="guest-rsvp">
      <div className="max-w-xl mx-auto px-6 py-12 md:py-20 space-y-10">
        {/* Hero */}
        <header className="text-center">
          <p className="eyebrow mb-3">Vous êtes convié(e) au mariage de</p>
          <h1 className="font-serif-luxe text-4xl md:text-5xl leading-tight" data-testid="rsvp-couple">
            <span className="italic gold-text">{ev.name || "Nos mariés"}</span>
          </h1>
          <p className="mt-4 text-white/70 text-sm md:text-base" data-testid="rsvp-event">
            {ev.date_fr}{ev.date_fr && (ev.lieu || ev.ville) ? " · " : ""}
            {ev.lieu}{ev.lieu && ev.ville ? ", " : ""}{ev.ville}
          </p>
        </header>

        {/* Personal greeting */}
        <section className="text-center border-t border-[#DCB457]/20 pt-8">
          <p className="text-white/60 text-sm">Bonjour</p>
          <p className="font-serif-luxe text-3xl mt-1 gold-text" data-testid="rsvp-hello">{g.prenom}</p>
        </section>

        {/* RSVP choices */}
        <section data-testid="rsvp-choices">
          <p className="eyebrow text-center mb-4">Votre réponse</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <button
              onClick={() => setRsvp("confirmed")}
              data-testid="rsvp-confirm"
              className={`card-luxe p-5 text-left transition ${rsvp === "confirmed" ? "border-[#DCB457] bg-[#DCB457]/10" : "hover:border-[#DCB457]/60"}`}
            >
              <CheckCircle2 size={20} className="text-[#DCB457] mb-2" strokeWidth={1.3} />
              <p className="font-serif-luxe text-lg">Je serai présent(e)</p>
              <p className="text-white/50 text-xs mt-1">Nous célébrons ensemble</p>
            </button>
            <button
              onClick={() => setRsvp("declined")}
              data-testid="rsvp-decline"
              className={`card-luxe p-5 text-left transition ${rsvp === "declined" ? "border-red-300/50 bg-red-300/10" : "hover:border-red-300/40"}`}
            >
              <XCircle size={20} className="text-red-300 mb-2" strokeWidth={1.3} />
              <p className="font-serif-luxe text-lg">Je ne pourrai pas venir</p>
              <p className="text-white/50 text-xs mt-1">Vous nous manquerez</p>
            </button>
          </div>
        </section>

        {/* Adults/Children — only if confirmed */}
        {rsvp === "confirmed" && (
          <section className="space-y-4" data-testid="rsvp-counts">
            <p className="eyebrow text-center">Nombre de personnes</p>
            <div className="grid grid-cols-2 gap-3">
              <Counter label="Adultes" value={adults} setValue={setAdults} testid="rsvp-adults" />
              <Counter label="Enfants" value={children} setValue={setChildren} testid="rsvp-children" />
            </div>
          </section>
        )}

        {/* Note */}
        {rsvp && (
          <section data-testid="rsvp-note-section">
            <p className="eyebrow mb-3">Un message pour les mariés ?</p>
            <textarea
              rows={4}
              maxLength={500}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              data-testid="rsvp-note"
              placeholder="Un petit mot doux, un besoin particulier, une chanson préférée…"
              className="w-full bg-black/50 border border-[#DCB457]/25 focus:border-[#DCB457] outline-none px-4 py-3 text-sm text-white/90"
            />
            <p className="text-[10px] text-white/40 text-right mt-1">{note.length} / 500</p>
          </section>
        )}

        {/* CTA */}
        {rsvp && (
          <div className="text-center">
            <button
              onClick={submit}
              disabled={submitting}
              data-testid="rsvp-submit"
              className="inline-flex items-center gap-2 bg-[#DCB457] text-black px-8 py-3 text-xs uppercase tracking-widest hover:bg-[#e6c454] disabled:opacity-40"
            >
              {submitting ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
              Confirmer ma réponse
            </button>
          </div>
        )}

        <footer className="text-center pt-8 border-t border-[#DCB457]/15">
          <p className="text-[10px] uppercase tracking-widest text-white/30">Merci pour votre réponse</p>
        </footer>
      </div>
    </div>
  );
}

function Counter({ label, value, setValue, testid }) {
  return (
    <div className="card-luxe p-4">
      <p className="text-[10px] uppercase tracking-widest text-white/50 mb-3 text-center">{label}</p>
      <div className="flex items-center justify-between">
        <button
          onClick={() => setValue(Math.max(0, Number(value) - 1))}
          data-testid={`${testid}-minus`}
          className="text-[#DCB457] hover:text-[#e6c454] disabled:opacity-30 p-1"
          disabled={value <= 0}
        ><MinusCircle size={22} strokeWidth={1.2} /></button>
        <span className="font-serif-luxe text-3xl" data-testid={`${testid}-value`}>{value}</span>
        <button
          onClick={() => setValue(Math.min(20, Number(value) + 1))}
          data-testid={`${testid}-plus`}
          className="text-[#DCB457] hover:text-[#e6c454] disabled:opacity-30 p-1"
          disabled={value >= 20}
        ><PlusCircle size={22} strokeWidth={1.2} /></button>
      </div>
    </div>
  );
}
